import { CUSTOMER_API_PREFIX } from '../contracts/customer-constants.js';
import {
  validateCustomerRequestDraft,
  type CustomerPhotoKind,
  type CustomerRequestDraft,
} from '../contracts/customer-services.js';
import type {
  CustomerRequestAttachmentResultDto,
  CustomerRequestItemDto,
  CustomerRequestPhotoResultDto,
  CustomerRequestResultDto,
} from '../contracts/customer-auth.js';
import type { AttributionLike } from '../contracts/dto.js';
import { customerServiceDefinition } from '../contracts/customer-services.js';
import type { CustomerServiceSlug } from '../contracts/customer-services.js';
import type { ResolvedCustomerClientConfig } from './config.js';
import { CustomerApiError } from './errors.js';
import { HttpTransport } from './http.js';

/**
 * The request API, as the UI sees it.
 *
 * `create()` takes a customer section key and the customer's own words. It does
 * not take a service id, a user id, a price, a currency or a specialist, because
 * the server resolves all of those. That is not a stylistic preference: it is the
 * control that stops a tampered widget from filing a request against a different
 * service, as a different customer, at a price of its choosing.
 */

export interface CreateCustomerRequestInput {
  service: CustomerServiceSlug;
  subject: string;
  details: string;
  answers?: Record<string, string>;
  idempotencyKey?: string;
  attribution?: AttributionLike;
}

export interface ListCustomerRequestsQuery {
  page?: number;
  pageSize?: number;
  service?: CustomerServiceSlug;
}

export class CustomerRequests {
  constructor(
    private readonly config: ResolvedCustomerClientConfig,
    private readonly transport: HttpTransport,
  ) {}

  /**
   * Validate a draft locally against the shared service definition.
   *
   * Purely a UX optimisation — the same function runs on the server, which is
   * what actually decides. Running it here means the customer sees every problem
   * at once instead of one round trip per field.
   */
  validate(draft: Partial<CustomerRequestDraft> | null | undefined): ReturnType<typeof validateCustomerRequestDraft> {
    return validateCustomerRequestDraft(draft);
  }

  /** File a request for the signed-in customer. */
  async create(input: CreateCustomerRequestInput): Promise<CustomerRequestResultDto> {
    const definition = customerServiceDefinition(input.service);
    if (!definition) {
      throw new CustomerApiError('UNKNOWN_SERVICE', `سرویس «${input.service}» شناخته‌شده نیست.`);
    }

    const issues = this.validate({
      service: input.service,
      subject: input.subject,
      details: input.details,
      answers: input.answers,
    });
    if (issues.length > 0) {
      throw new CustomerApiError('VALIDATION_ERROR', 'اطلاعات درخواست کامل نیست.', {
        fieldIssues: issues,
      });
    }

    const body = {
      service: input.service,
      subject: input.subject.trim(),
      details: input.details.trim(),
      answers: input.answers ?? {},
      attribution: input.attribution ?? this.config.attribution ?? {},
      ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
    };

    const { data } = await this.transport.send<CustomerRequestResultDto>({
      path: this.config.authMode === 'sso' ? this.bridgeUrl('requestUrl') : `${CUSTOMER_API_PREFIX}/requests`,
      method: 'POST',
      target: this.config.authMode === 'sso' ? 'bridge' : 'authority',
      body,
      ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
      // A create is never retried implicitly: a duplicate request is worse than
      // an error the customer can see and retry deliberately. The idempotency
      // key, when present, makes an explicit retry safe.
      retryable: false,
    });

    this.config.onEvent?.({
      name: 'request.submitted',
      service: input.service,
      detail: { reused: data.reused, requestPublicId: data.request?.publicId },
    });
    return data;
  }

  /** The signed-in customer's own requests. */
  async list(query: ListCustomerRequestsQuery = {}): Promise<CustomerRequestItemDto[]> {
    const path =
      this.config.authMode === 'sso' && this.config.bridge?.listUrl
        ? this.bridgeUrl('listUrl')
        : `${CUSTOMER_API_PREFIX}/requests`;
    const { data } = await this.transport.send<
      { items?: CustomerRequestItemDto[] } | CustomerRequestItemDto[]
    >({
      path,
      method: 'GET',
      target: this.config.authMode === 'sso' && this.config.bridge?.listUrl ? 'bridge' : 'authority',
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
        ...(query.service ? { service: query.service } : {}),
      },
    });
    return Array.isArray(data) ? data : (data.items ?? []);
  }

  /** True when this deployment can accept body-photo uploads directly. */
  get photoUploadAvailable(): boolean {
    return this.config.authMode === 'same-site';
  }

  /**
   * Upload one optional body photo for a request the customer owns.
   *
   * Multipart, so the browser sets the boundary; the session cookie and CSRF
   * header are added by the transport. Re-uploading the same view replaces the
   * previous photo server-side, so a blurry shot can be retaken.
   */
  async uploadPhoto(
    requestReference: string,
    input: { kind: CustomerPhotoKind; file: Blob },
  ): Promise<CustomerRequestPhotoResultDto> {
    const reference = requestReference.trim();
    if (!reference) {
      throw new CustomerApiError(
        'VALIDATION_ERROR',
        'شناسه درخواست برای بارگذاری تصویر مشخص نیست.',
      );
    }
    const form = new FormData();
    const name =
      typeof File !== 'undefined' && input.file instanceof File && input.file.name
        ? input.file.name
        : 'photo';
    form.append('photo', input.file, name);
    form.append('kind', input.kind);

    const { data } = await this.transport.send<CustomerRequestPhotoResultDto>({
      path: `${CUSTOMER_API_PREFIX}/requests/${encodeURIComponent(reference)}/photos`,
      method: 'POST',
      target: 'authority',
      form,
      // An upload is expensive and never idempotent on retry, so a transport
      // failure is surfaced rather than swallowed and repeated.
      retryable: false,
    });
    return data;
  }

  /** True when this deployment can accept optional request attachments directly. */
  get attachmentUploadAvailable(): boolean {
    return this.config.authMode === 'same-site';
  }

  /**
   * Upload one optional supporting attachment for a request the customer owns.
   *
   * Server enforces the three-file / 5 MiB / image+PDF policy. The client still
   * validates before send so the customer sees Persian errors immediately.
   */
  async uploadAttachment(
    requestReference: string,
    input: { file: Blob },
  ): Promise<CustomerRequestAttachmentResultDto> {
    const reference = requestReference.trim();
    if (!reference) {
      throw new CustomerApiError(
        'VALIDATION_ERROR',
        'شناسه درخواست برای بارگذاری پیوست مشخص نیست.',
      );
    }
    const form = new FormData();
    const name =
      typeof File !== 'undefined' && input.file instanceof File && input.file.name
        ? input.file.name
        : 'attachment';
    form.append('attachment', input.file, name);

    const { data } = await this.transport.send<CustomerRequestAttachmentResultDto>({
      path: `${CUSTOMER_API_PREFIX}/requests/${encodeURIComponent(reference)}/attachments`,
      method: 'POST',
      target: 'authority',
      form,
      retryable: false,
    });
    return data;
  }

  private bridgeUrl(key: 'requestUrl' | 'listUrl'): string {
    const url = this.config.bridge?.[key];
    if (!url) {
      throw new CustomerApiError(
        'BRIDGE_UNAVAILABLE',
        'ثبت درخواست از این سایت پیکربندی نشده است.',
      );
    }
    return url;
  }
}
